import React, { useEffect } from 'react';
import { format, toZonedTime } from 'date-fns-tz';
import { useDispatch } from 'react-redux';
import {
    removeHighlight,
    setHighlights,
} from '../../features/highlights/highlightsSlice';
import DotIcon from '../../assets/images/DotIcon.svg';
import CloseIcon from '../../assets/images/closeIcon.svg';
import DeleteIcon from '../../assets/images/DeleteIcon.svg';
import RightArrowIcon from '../../assets/images/righArrowIcon.svg';
import {
    useDeleteHighlightMutation,
    useGetHighlightsQuery,
} from '../../api/highlights';
import {
    goTOPageHref,
    handlePageChange,
    removeHighlightText,
} from '../../utils/readiumUtils';
import { Modal } from 'antd';

interface Highlight {
    id: number;
    book_id: number;
    selection: string;
    cfi: string;
    page_no: number;
    href: string;
    properties: {
        colour: string;
        underline: boolean;
    };
    is_active: boolean;
    created_at: string;
    updated_at: string;
    chapter_name: string;
}
interface AnnotationLibraryModalProps {
    onClose: () => void;
    bookId: string;
    offset: number;
}

const AnnotationLibraryModal: React.FC<AnnotationLibraryModalProps> = ({
    onClose,
    bookId,
    offset,
}) => {
    const dispatch = useDispatch();

    const { data: highlights = [], refetch } =
        useGetHighlightsQuery<any>(bookId);
    dispatch(setHighlights(highlights?.data));
    const [deleteHighlight, { isSuccess: isDeleteSuccess }] =
        useDeleteHighlightMutation();

    // Show confirmation modal before deleting
    const confirmDelete = (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this annotation?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => handleDelete(id),
        });
    };
    const handleDelete = async (id: number) => {
        try {
            await deleteHighlight({ id, bookId });
            dispatch(removeHighlight(id));
            removeHighlightText(window, id);
        } catch (error) {
            console.error('Failed to delete highlight:', error);
        }
    };
    const handleRightArrowClick = (href: string) => {
        goTOPageHref(window, href);
        handlePageChange(window)
    };
    useEffect(() => {
        refetch();
    }, [isDeleteSuccess, refetch]);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[60rem] h-[30rem] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center sticky top-0 bg-white z-10 border-gray-200 pb-2">
                    <h2 className="text-xl font-bold">Annotation Library</h2>
                    <button
                        className="text-gray-500 hover:text-gray-700"
                        data-testid="annotation-library-close-button"
                        onClick={onClose}
                    >
                        <img src={CloseIcon} alt="Close" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1 mt-4 custom-scrollbar">
                    {highlights?.data?.length > 0 ? (
                        <ul className="space-y-4">
                            {highlights?.data.map(
                                (highlight: Highlight, index: number) => (
                                    <li
                                        onClick={() => {
                                            handleRightArrowClick(
                                                highlight.href
                                            );
                                            handlePageChange(window);
                                            onClose();
                                        }}
                                        key={highlight.id}
                                        className="flex items-center justify-between p-4 rounded-xl border border-gray-300 shadow-sm hover:bg-[#F7F9FD] hover:cursor-pointer"
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center justify-center w-12 h-10 bg-blue-100 rounded-lg">
                                                <span className="text-black font-bold text-lg">
                                                    {index + 1}
                                                </span>
                                            </div>

                                            <div className="flex flex-col">
                                                <div>
                                                    <h3 className="font-semibold text-black">
                                                        {highlight.selection}
                                                    </h3>
                                                </div>
                                                <div className="flex space-x-4">
                                                    {highlight.chapter_name && (
                                                        <p className="text-sm text-gray-600 flex flex-row">
                                                            <div>
                                                                {
                                                                    highlight.chapter_name
                                                                }
                                                            </div>
                                                        </p>
                                                    )}
                                                    <p className="text-sm text-gray-600 flex flex-row space-x-2">
                                                        {highlight.chapter_name && (
                                                            <img
                                                                src={DotIcon}
                                                                alt=""
                                                            />
                                                        )}
                                                        <div>
                                                            Page no:
                                                            {highlight.page_no <=
                                                                0
                                                                ? 1
                                                                : highlight.page_no}
                                                        </div>
                                                    </p>
                                                    <p className="text-sm text-gray-600 flex flex-row space-x-2">
                                                        <img
                                                            src={DotIcon}
                                                            alt=""
                                                        />
                                                        <div className="text-nowrap">
                                                            {format(
                                                                toZonedTime(
                                                                    new Date(
                                                                        highlight.created_at
                                                                    ),
                                                                    'Asia/Kolkata'
                                                                ),
                                                                'dd MMM yyyy, hh:mm a',
                                                                {
                                                                    timeZone:
                                                                        'Asia/Kolkata',
                                                                }
                                                            )}
                                                        </div>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-12">
                                            <button
                                                data-testid="annotation-library-item-delete-button"
                                                className=" hover:bg-blue-100 rounded-full p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    confirmDelete(highlight.id);
                                                }}
                                            >
                                                <img
                                                    src={DeleteIcon}
                                                    alt="Delete"
                                                />
                                            </button>
                                            <button
                                                className="text-gray-500 hover:text-gray-700"
                                                data-testid="annotation-library-item-goto-button"
                                            >
                                                <img
                                                    src={RightArrowIcon}
                                                    alt="Arrow"
                                                />
                                            </button>
                                        </div>
                                    </li>
                                )
                            )}
                        </ul>
                    ) : (
                        <p className="text-gray-500 text-center">
                            No annotations saved.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnnotationLibraryModal;
